import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoiceApi, stripeConnectApi, creditMemoApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft,
  Send,
  Link2,
  CheckCircle,
  Check,
  Download,
  Copy,
  Trash2,
  Edit,
  FileText,
  Clock,
  AlertCircle,
  Eye,
  DollarSign,
  ReceiptText,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-100 text-purple-700', icon: Eye },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700', icon: DollarSign },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: Trash2 },
};

export default function InvoiceDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [copyToSelf, setCopyToSelf] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchInvoice();
    invoiceApi.getSettings()
      .then((res) => setCopyToSelf(Boolean(res.data?.email_copy_to_self)))
      .catch(() => {});
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await invoiceApi.getOne(id);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!invoice.client_email) {
      toast.error('Client has no email address');
      return;
    }
    setSending(true);
    try {
      await invoiceApi.send(invoice.id, copyToSelf);
      toast.success(
        copyToSelf
          ? `Invoice sent to ${invoice.client_email} (copy to you)`
          : `Invoice sent to ${invoice.client_email}`
      );
      setSendOpen(false);
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
    }
  };

  const handleFinalize = async () => {
    try {
      await invoiceApi.finalize(invoice.id);
      toast.success('Invoice finalized (not emailed)');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to finalize invoice');
    }
  };

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNote, setPaymentNote] = useState('');

  const [cmOpen, setCmOpen] = useState(false);
  const [cmReason, setCmReason] = useState('');
  const [cmSaving, setCmSaving] = useState(false);

  const handleCreateCreditMemo = async () => {
    const amount = Math.round(((invoice.paid_amount || 0) - (invoice.total || 0)) * 100) / 100;
    if (amount <= 0) { toast.error('No refund is due on this invoice'); return; }
    setCmSaving(true);
    try {
      const res = await creditMemoApi.create({
        client_id: invoice.client_id,
        invoice_id: invoice.id,
        amount,
        reason: cmReason.trim() || 'Overpayment refund',
        notes: `For invoice ${invoice.invoice_number}`,
      });
      toast.success(`Credit memo ${res.data.credit_memo_number} created`);
      setCmOpen(false);
      const pdf = await creditMemoApi.getPdf(res.data.id);
      const url = window.URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create credit memo');
    } finally {
      setCmSaving(false);
    }
  };

  const handleMarkPaid = async (amount) => {
    try {
      const total = invoice.total || 0;
      const prevPaid = invoice.paid_amount || 0;
      const payAmount = amount ? parseFloat(amount) : undefined;
      await invoiceApi.markPaid(invoice.id, payAmount, paymentMethod || undefined, paymentNote || undefined);
      const isPartial = payAmount != null && (prevPaid + payAmount) + 0.005 < total;
      toast.success(isPartial ? `Payment of $${payAmount.toFixed(2)} recorded` : 'Invoice marked as paid');
      setPaymentOpen(false);
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentNote('');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Remove this payment from the history? Totals will be recalculated.')) return;
    try {
      await invoiceApi.deletePayment(invoice.id, paymentId);
      toast.success('Payment removed');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to remove payment');
    }
  };

  const handleDelete = async () => {
    const isPaid = invoice.status === 'paid';
    const msg = isPaid
      ? `Cancel PAID invoice ${invoice.invoice_number}? This removes it from your paid totals (use for accidental payments).`
      : `Cancel invoice ${invoice.invoice_number}?`;
    if (!window.confirm(msg)) return;
    try {
      await invoiceApi.delete(invoice.id);
      toast.success('Invoice cancelled');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to cancel invoice');
    }
  };

  const handlePermanentDelete = async () => {
    if (!window.confirm(`Permanently delete ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await invoiceApi.permanentDelete(invoice.id);
      toast.success('Invoice permanently deleted');
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await invoiceApi.duplicate(invoice.id);
      toast.success(`Created ${response.data.invoice_number}`);
      navigate(`/invoices/${response.data.id}/edit`);
    } catch (error) {
      toast.error('Failed to duplicate invoice');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await invoiceApi.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleCopyPayLink = async () => {
    try {
      const res = await stripeConnectApi.getInvoicePaymentLink(invoice.id);
      await navigator.clipboard.writeText(res.data.link);
      if (!res.data.org_connected) {
        toast.warning('Link copied — connect Stripe in Settings → Online Payments to enable the Pay button.');
      } else {
        toast.success('Payment link copied to clipboard');
      }
    } catch (e) {
      toast.error('Could not generate payment link');
    }
  };

  const handleViewPdf = async () => {
    try {
      const response = await invoiceApi.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view PDF');
    }
  };

  const handleViewInternalPdf = async () => {
    try {
      const response = await invoiceApi.getPdf(invoice.id, true);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view internal PDF');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const StatusIcon = STATUS_CONFIG[invoice.status]?.icon || FileText;
  const refundDue = Math.round(((invoice.paid_amount || 0) - (invoice.total || 0)) * 100) / 100;
  const balanceDue = Math.round(((invoice.total || 0) - (invoice.paid_amount || 0)) * 100) / 100;

  return (
    <div className="space-y-6" data-testid="invoice-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{invoice.invoice_number}</h1>
              <Badge className={`${STATUS_CONFIG[invoice.status]?.color} gap-1`}>
                <StatusIcon className="h-3 w-3" />
                {STATUS_CONFIG[invoice.status]?.label}
              </Badge>
            </div>
            <p className="text-muted-foreground">{invoice.client_name}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleViewPdf} data-testid="view-client-pdf-btn">
            <Eye className="h-4 w-4 mr-2" />
            Client PDF
          </Button>
          <Button variant="outline" onClick={handleViewInternalPdf} data-testid="view-internal-pdf-btn">
            <FileText className="h-4 w-4 mr-2" />
            Internal
          </Button>
          <Button variant="outline" onClick={handleDownloadPdf}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          {['draft', 'sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) && (
            <Button variant="outline" onClick={() => navigate(`/invoices/${invoice.id}/edit`)} data-testid="edit-invoice-btn">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {['draft', 'sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) && (
            <Button variant="outline" onClick={handleCopyPayLink} data-testid="copy-pay-link-btn">
              <Link2 className="h-4 w-4 mr-2" />
              Copy Pay Link
            </Button>
          )}
          {invoice.status === 'draft' && (
            <Button variant="outline" onClick={handleFinalize} data-testid="finalize-invoice-btn">
              <Check className="h-4 w-4 mr-2" />
              Finalize (don't send)
            </Button>
          )}
          {['draft', 'sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) && (
            <Button onClick={() => setSendOpen(true)} data-testid="send-invoice-btn">
              <Send className="h-4 w-4 mr-2" />
              {invoice.status === 'draft' ? 'Send' : invoice.status === 'partial' ? 'Send Balance' : 'Resend'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Preview */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">Invoice Details</CardTitle>
                  {invoice.project_name && (
                    <p className="text-muted-foreground">Project: {invoice.project_name}</p>
                  )}
                </div>
                <div className="text-right text-sm">
                  <p>
                    <span className="text-muted-foreground">Issue Date: </span>
                    {formatDate(invoice.issue_date)}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Due Date: </span>
                    {formatDate(invoice.due_date)}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bill To */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">BILL TO</p>
                <p className="font-medium">{invoice.client_name}</p>
                {invoice.client_address && (
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {invoice.client_address}
                  </p>
                )}
                {invoice.client_email && (
                  <p className="text-sm text-muted-foreground">{invoice.client_email}</p>
                )}
              </div>

              <Separator />

              {/* Line Items */}
              <div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">Description</th>
                      <th className="text-right py-2 font-medium w-20">Qty</th>
                      <th className="text-right py-2 font-medium w-28">Rate</th>
                      <th className="text-right py-2 font-medium w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-3">{item.description}</td>
                        <td className="py-3 text-right">{item.quantity}</td>
                        <td className="py-3 text-right">{formatCurrency(item.unit_price)}</td>
                        <td className="py-3 text-right">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(invoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax ({invoice.tax_rate}%)</span>
                    <span>{formatCurrency(invoice.tax_amount)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>{formatCurrency(invoice.total)}</span>
                  </div>
                  {invoice.amount_due != null && invoice.amount_due !== invoice.total && !(invoice.paid_amount > 0) && (
                    <div className="flex justify-between font-bold text-blue-700 border-t pt-2" data-testid="amount-due-this-invoice">
                      <span>Amount Due This Invoice{invoice.bill_pct ? ` (${invoice.bill_pct}%)` : ''}</span>
                      <span>{formatCurrency(invoice.amount_due)}</span>
                    </div>
                  )}
                  {invoice.paid_amount > 0 && (
                    <>
                      <div className="flex justify-between text-green-600 border-t pt-2">
                        <span>Amount Paid</span>
                        <span>-{formatCurrency(invoice.paid_amount)}</span>
                      </div>
                      {balanceDue > 0.005 && (
                        <div className="flex justify-between font-bold" data-testid="balance-due">
                          <span>Balance Due</span>
                          <span>{formatCurrency(balanceDue)}</span>
                        </div>
                      )}
                    </>
                  )}
                  {refundDue > 0.005 && (
                    <div className="flex justify-between font-bold text-blue-700 border-t pt-2" data-testid="refund-due">
                      <span>Refund Due to Client</span>
                      <span>{formatCurrency(refundDue)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {invoice.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">NOTES</p>
                    <p className="text-sm">{invoice.notes}</p>
                  </div>
                </>
              )}

              {/* Terms */}
              {invoice.terms && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">PAYMENT TERMS</p>
                  <p className="text-sm">{invoice.terms}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${STATUS_CONFIG[invoice.status]?.color}`}>
                  <StatusIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{STATUS_CONFIG[invoice.status]?.label}</p>
                  <p className="text-sm text-muted-foreground">
                    {invoice.status === 'paid' && invoice.paid_at
                      ? `Paid ${formatDateTime(invoice.paid_at)}`
                      : invoice.status === 'sent' && invoice.sent_at
                      ? `Sent ${formatDateTime(invoice.sent_at)}`
                      : invoice.status === 'sent' && invoice.issued_at
                      ? `Issued ${formatDateTime(invoice.issued_at)} — not emailed`
                      : `Created ${formatDateTime(invoice.created_at)}`}
                  </p>
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span>Created {formatDateTime(invoice.created_at)}</span>
                </div>
                {invoice.sent_at && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span>Sent {formatDateTime(invoice.sent_at)}</span>
                  </div>
                )}
                {invoice.viewed_at && (
                  <div className="flex items-center gap-2" data-testid="invoice-viewed-timeline">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span>Viewed by client {formatDateTime(invoice.viewed_at)}</span>
                  </div>
                )}
                {invoice.paid_at && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span>Paid {formatDateTime(invoice.paid_at)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {['sent', 'viewed', 'overdue', 'partial'].includes(invoice.status) && (
                <>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleMarkPaid()}
                    data-testid="mark-paid-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                    Mark as Paid (Full)
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => { setPaymentAmount(''); setPaymentOpen(true); }}
                    data-testid="record-payment-btn"
                  >
                    <DollarSign className="h-4 w-4 mr-2 text-amber-600" />
                    Record Partial Payment
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleDuplicate}
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate Invoice
              </Button>
              {refundDue > 0.005 && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-blue-700 hover:text-blue-800"
                  onClick={() => { setCmReason('Overpayment refund'); setCmOpen(true); }}
                  data-testid="create-credit-memo-btn"
                >
                  <ReceiptText className="h-4 w-4 mr-2" />
                  Create Credit Memo ({formatCurrency(refundDue)})
                </Button>
              )}
              {invoice.status !== 'cancelled' && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:text-red-700"
                  onClick={handleDelete}
                  data-testid="cancel-invoice-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Cancel Invoice
                </Button>
              )}
              {invoice.status === 'cancelled' && (
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:text-red-700"
                  onClick={handlePermanentDelete}
                  data-testid="delete-invoice-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Permanently
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <Card data-testid="payment-history-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  Payment History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {invoice.payments.map((p) => (
                  <div key={p.id} className="flex items-start justify-between gap-2 border-b last:border-0 pb-2 last:pb-0 group" data-testid={`payment-row-${p.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-sm tabular-nums">{formatCurrency(p.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(p.date)}
                        {p.method ? ` · ${p.method}` : ''}
                        {p.note ? ` · ${p.note}` : ''}
                      </p>
                      {p.recorded_by_name && (
                        <p className="text-xs text-muted-foreground/70">by {p.recorded_by_name}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      className="h-7 w-7 p-0 text-destructive opacity-0 group-hover:opacity-100"
                      onClick={() => handleDeletePayment(p.id)}
                      data-testid={`delete-payment-${p.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1 text-sm">
                  <span className="text-muted-foreground">Total Received</span>
                  <span className="font-semibold text-green-600 tabular-nums">{formatCurrency(invoice.paid_amount || 0)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Created By */}
          {invoice.created_by_name && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Created by</p>
                <p className="font-medium">{invoice.created_by_name}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Partial Payment Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Record a payment received on this invoice. Payments accumulate toward the total.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Invoice Total: <span className="font-semibold text-foreground">${(invoice?.total || 0).toFixed(2)}</span></p>
              {invoice?.amount_due != null && invoice.amount_due !== invoice.total && (
                <p className="text-sm text-muted-foreground">Billed This Invoice: <span className="font-semibold text-foreground">${invoice.amount_due.toFixed(2)}</span></p>
              )}
              {invoice?.paid_amount > 0 && (
                <p className="text-sm text-muted-foreground">Previously Paid: <span className="font-semibold text-green-600">${invoice.paid_amount.toFixed(2)}</span></p>
              )}
              <p className="text-sm text-muted-foreground">Remaining: <span className="font-semibold text-foreground">${Math.max(0, (invoice?.total || 0) - (invoice?.paid_amount || 0)).toFixed(2)}</span></p>
            </div>
            <div>
              <Label>Payment Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={`e.g. ${Math.max(0, (invoice?.total || 0) - (invoice?.paid_amount || 0)).toFixed(2)}`}
                data-testid="payment-amount-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Enter any amount — over/under payments (rounding) are allowed. Adds to previous payments.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Method (optional)</Label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full h-10 mt-1 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="payment-method-select"
                >
                  <option value="">—</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Card">Card</option>
                  <option value="Venmo">Venmo</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="e.g. Check #1234"
                  className="mt-1"
                  data-testid="payment-note-input"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPaymentOpen(false)}>Cancel</Button>
              <Button
                onClick={() => handleMarkPaid(paymentAmount)}
                disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="confirm-payment-btn"
              >
                Record ${paymentAmount || '0.00'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Credit Memo Dialog */}
      <Dialog open={cmOpen} onOpenChange={setCmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Credit Memo</DialogTitle>
            <DialogDescription>
              This invoice was credited below what the client already paid, so {formatCurrency(refundDue)} is owed back to them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Refund Due: <span className="font-semibold text-blue-700">{formatCurrency(refundDue)}</span></p>
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={cmReason}
                onChange={(e) => setCmReason(e.target.value)}
                placeholder="e.g. Overpayment refund"
                data-testid="cm-reason-invoice-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCmOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateCreditMemo} disabled={cmSaving} data-testid="cm-create-invoice-btn">
                Create &amp; Open PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Send Invoice Dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {invoice.status === 'partial' ? 'Send Balance' : invoice.status === 'draft' ? 'Send Invoice' : 'Resend Invoice'}
            </DialogTitle>
            <DialogDescription>
              Email {invoice.invoice_number} to {invoice.client_email || 'the client'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={copyToSelf}
                onCheckedChange={(c) => setCopyToSelf(c === true)}
                data-testid="send-copy-to-self-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm">
                Send a copy to me (BCC)
                {user?.email && <span className="block text-xs text-muted-foreground">{user.email}</span>}
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
              <Button onClick={handleSend} disabled={sending} data-testid="confirm-send-btn">
                <Send className="h-4 w-4 mr-2" />
                {invoice.status === 'partial' ? 'Send Balance' : invoice.status === 'draft' ? 'Send' : 'Resend'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
